/**
 * Search Suggestions Hook for v2v2 SWFL Farmers Market Application
 * Debounced suggestion generation with keyboard navigation support
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { SearchSuggestion, SuggestionGroup } from '@/types/suggestions';
import { 
  getSuggestionsForQuery, 
  getRecentSearches, 
  saveRecentSearch, 
  clearRecentSearches as clearRecentSearchesUtil,
  flattenSuggestions 
} from '@/lib/suggestions';

const DEBOUNCE_DELAY = 150;
const MIN_QUERY_LENGTH = 0;

export function useSuggestions(query: string) {
  const [suggestions, setSuggestions] = useState<SuggestionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Load recent searches on mount
  useEffect(() => {
    mountedRef.current = true;
    setRecentSearches(getRecentSearches());
    
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch suggestions when query changes
  useEffect(() => {
    if (!mountedRef.current) return;

    const fetchSuggestions = async () => {
      // Clear previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Skip if query is too short and no recent searches to show
      if (query.length < MIN_QUERY_LENGTH && query.length > 0) {
        setSuggestions([]);
        setSelectedIndex(-1);
        return;
      }

      // Debounce the fetch
      timeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;

        setIsLoading(true);
        try {
          const groups = await getSuggestionsForQuery(query);
          if (mountedRef.current) {
            setSuggestions(groups);
            setSelectedIndex(-1);
          }
        } catch (error) {
          if (mountedRef.current) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
          }
        } finally {
          if (mountedRef.current) {
            setIsLoading(false);
          }
        }
      }, DEBOUNCE_DELAY);
    };

    fetchSuggestions();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query]);

  // Handle suggestion selection
  const onSelect = useCallback((suggestion: SearchSuggestion) => {
    // Save to recent searches
    saveRecentSearch(suggestion.text);
    setRecentSearches(getRecentSearches());
  }, []);

  // Add a recent search
  const addRecentSearch = useCallback((searchQuery: string) => {
    saveRecentSearch(searchQuery);
    setRecentSearches(getRecentSearches());
  }, []);

  // Clear all recent searches
  const clearRecentSearches = useCallback(() => {
    clearRecentSearchesUtil();
    setRecentSearches([]);
  }, []);

  // Navigate through suggestions with keyboard
  const navigateSuggestions = useCallback((direction: 'up' | 'down' | 'first' | 'last') => {
    const allSuggestions = flattenSuggestions(suggestions);
    const totalCount = allSuggestions.length;
    
    if (totalCount === 0) {
      setSelectedIndex(-1);
      return;
    }

    setSelectedIndex(prev => {
      if (direction === 'first') return 0;
      if (direction === 'last') return totalCount - 1;
      if (direction === 'down') return prev < totalCount - 1 ? prev + 1 : 0;
      return prev > 0 ? prev - 1 : totalCount - 1;
    });
  }, [suggestions]);

  // Get the currently selected suggestion
  const selectedSuggestion = useCallback(() => {
    const allSuggestions = flattenSuggestions(suggestions);
    if (selectedIndex >= 0 && selectedIndex < allSuggestions.length) {
      return allSuggestions[selectedIndex];
    }
    return null;
  }, [suggestions, selectedIndex]);

  return {
    suggestions,
    isLoading,
    selectedIndex,
    setSelectedIndex,
    onSelect,
    addRecentSearch,
    clearRecentSearches,
    recentSearches,
    navigateSuggestions,
    selectedSuggestion,
  };
}
